import React, { useState } from "react";
import InputPanel from "./components/InputPanel";
import WinnerDisplay from "./components/WinnerDisplay";
import ConfigPanel from "./components/ConfigPanel";
import WalletConnectButton from "./components/WalletConnectButton";
import Verify from "./components/Verify";
import { ethers } from "ethers";
import abi from "./blockchain/ProvablyFairRNG.abi.json";
import blockchainConfig from "./blockchain/config";

// Ensure global Buffer and process are set if needed
import { Buffer } from "buffer";
global.Buffer = Buffer;
global.process = require("process");

const App = () => {
  const [walletAddress, setWalletAddress] = useState(null);
  const [operatorAddress, setOperatorAddress] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [winners, setWinners] = useState([]);
  const [config, setConfig] = useState(null);
  const [secret, setSecret] = useState("");
  const [isRevealPhase, setIsRevealPhase] = useState(false);
  const [isWinnerSelected, setIsWinnerSelected] = useState(false);
  const [signer, setSigner] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showVerify, setShowVerify] = useState(false);

  const explorerUrlBase = "https://explore.steamexchange.io"; // Blockchain explorer base URL

  const connectWalletHandler = async () => {
    if (!window.ethereum) {
      console.error("MetaMask is not installed!");
      setStatusMessage("MetaMask is not installed. Please install it to use this app.");
      return;
    }
    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const address = await signer.getAddress();
      setWalletAddress(address);
      setSigner(signer);
      console.log("Wallet connected:", address);
      setStatusMessage(`Wallet connected: ${address}`);

      // Get the operator's address (contract owner) from the contract.
      const contract = new ethers.Contract(blockchainConfig.contractAddress, abi, signer);
      const owner = await contract.owner();
      setOperatorAddress(owner);
      console.log("Operator (owner) address:", owner);
    } catch (error) {
      console.error("Error connecting wallet:", error);
      setStatusMessage("Error connecting wallet. Check the console for details.");
    }
  };

  const handleConfigSubmit = async (configData) => {
    setConfig(configData);
    if (!signer) {
      console.error("Please connect your wallet first!");
      setStatusMessage("Please connect your wallet first!");
      return;
    }
    if (!secret) {
      console.error("Generate a secret before submitting the configuration.");
      setStatusMessage("Generate a secret before submitting the configuration.");
      return;
    }
    if (sessionId === null) {
      console.error("Session ID missing. Ensure you started a new session.");
      setStatusMessage("Session ID missing. Ensure you started a new session.");
      return;
    }
    try {
      setStatusMessage(`Configuration submitted for session ${sessionId}!`);
      console.log("Configuration submitted for session", sessionId);
    } catch (error) {
      console.error("Error submitting configuration:", error);
      setStatusMessage(`Error submitting configuration: ${error.message}`);
    }
  };

  const handleBulkCommit = async (inputAddresses) => {
    setAddresses(inputAddresses);
    setStatusMessage("Bulk commitments successfully submitted!");
  };

  const handleStartRevealPhase = async () => {
    if (!signer || sessionId === null) {
      console.error("Missing signer or sessionId");
      setStatusMessage("Missing signer or sessionId");
      return;
    }
    try {
      const contract = new ethers.Contract(blockchainConfig.contractAddress, abi, signer);
      const tx = await contract.startRevealPhase(sessionId);
      await tx.wait();
      setIsRevealPhase(true);
      setStatusMessage("Reveal phase started! Participants can now reveal their secrets.");
      console.log("Reveal phase started for session", sessionId);
    } catch (error) {
      console.error("Error starting reveal phase:", error);
      setStatusMessage(`Error starting reveal phase: ${error.message}`);
    }
  };

  const handleRevealSecret = async () => {
    if (!secret || !signer || sessionId === null) {
      console.error("Missing secret, sessionId, or signer");
      setStatusMessage("Missing secret, sessionId, or signer");
      return;
    }
  
    try {
      console.log("Revealing secret:", secret);
      const secretBytes = ethers.getBytes(secret);
      console.log("Secret byte array:", secretBytes);
      console.log("Secret byte length:", secretBytes.length);
  
      const formattedSecret = secret;
  
      const contract = new ethers.Contract(blockchainConfig.contractAddress, abi, signer);
      const tx = await contract.reveal(sessionId, formattedSecret);
      await tx.wait();
      setStatusMessage("Secret revealed! You can now select winners.");
      console.log("Secret revealed for session", sessionId);
    } catch (error) {
      console.error("Error revealing secret:", error);
      setStatusMessage(`Error revealing secret: ${error.message}`);
    }
  };
  
  const handleSelectWinners = async () => {
    if (!config || !config.numWinners || !signer || sessionId === null) {
      console.error("Missing configuration, signer, or sessionId");
      setStatusMessage("Missing configuration, signer, or sessionId");
      return;
    }
    try {
      const contract = new ethers.Contract(blockchainConfig.contractAddress, abi, signer);
      const tx = await contract.selectWinners(sessionId, config.numWinners);
      await tx.wait();

      const blockchainWinners = await contract.getWinners(sessionId);
      setWinners(blockchainWinners);
      setIsWinnerSelected(true);
      setStatusMessage("Winners selected successfully!");
      console.log("Winners selected for session", sessionId, blockchainWinners);
    } catch (error) {
      console.error("Error selecting winners:", error);
      setStatusMessage(`Error selecting winners: ${error.message}`);
    }
  };

  const navigateToHomepage = () => {
    window.location.href = "/";
  };

  return (
    <div className="flex flex-col justify-between min-h-screen bg-gradient-to-r from-gray-900 to-black">
      {/* Header */}
      <header className="container flex flex-col items-center justify-between px-4 py-4 mx-auto sm:flex-row">
        <img
          src="/img/logo.png"
          alt="Logo"
          className="w-16 h-auto cursor-pointer sm:w-20 md:w-24"
          onClick={navigateToHomepage}
        />
        <div className="flex flex-col items-center mt-4 space-y-3 sm:flex-row sm:mt-0 sm:space-y-0 sm:space-x-4">
          <WalletConnectButton onWalletConnected={connectWalletHandler} />
          <button
            onClick={() => setShowVerify(true)}
            className="px-4 py-2 text-sm font-semibold text-white bg-green-800 rounded-lg hover:bg-blue-600"
          >
            Verify Results
          </button>
        </div>
      </header>
  
      {/* Main Content */}
      <main className="container flex flex-col items-center justify-center flex-1 px-4 mx-auto space-y-6">
        {!config ? (
          <ConfigPanel
            onSubmit={handleConfigSubmit}
            onGenerateSecret={setSecret}
            signer={signer}
            setSessionId={setSessionId}
          />
        ) : !addresses.length ? (
          <InputPanel
            onCommitComplete={handleBulkCommit}
            sessionId={sessionId}
            secret={secret}
          />
        ) : (
          <div className="flex flex-col items-center space-y-4 sm:flex-row sm:space-y-0 sm:space-x-6">
            {!isRevealPhase ? (
              <button
                onClick={handleStartRevealPhase}
                className="px-4 py-2 text-xs font-semibold text-white bg-purple-500 rounded-lg hover:bg-purple-600 sm:px-8 sm:py-3 sm:text-sm"
              >
                Start Selection Process
              </button>
            ) : !isWinnerSelected ? (
              <div className="flex flex-col items-center space-y-4">
                {walletAddress &&
                operatorAddress &&
                walletAddress.toLowerCase() === operatorAddress.toLowerCase() ? (
                  <p className="text-xs text-center sm:text-sm md:text-base">
                    You are the operator. Your address cannot participate as a contestant.
                  </p>
                ) : (
                  <button
                    onClick={handleRevealSecret}
                    className="px-4 py-2 text-xs font-semibold text-white bg-green-500 rounded-lg hover:bg-green-600 sm:px-8 sm:py-3 sm:text-sm"
                  >
                    Reveal Seed Response
                  </button>
                )}
                <button
                  onClick={handleSelectWinners}
                  className="px-4 py-2 text-xs font-semibold text-white bg-indigo-500 rounded-lg hover:bg-indigo-600 sm:px-8 sm:py-3 sm:text-sm"
                >
                  Select Winners
                </button>
              </div>
            ) : (
              <WinnerDisplay
                winners={winners}
                config={config}
                sessionId={sessionId}
                explorerUrlBase={explorerUrlBase}
              />
            )}
          </div>
        )}
        {statusMessage && (
          <p className="px-4 text-xs text-center text-white sm:text-sm md:text-base">
            {statusMessage}
          </p>
        )}
      </main>
  
      {/* Footer */}
      <footer className="container px-4 py-4 mx-auto text-center">
        <p className="text-xs text-gray-400 sm:text-sm">
          Made with <span className="text-red-500">&hearts;</span> by Steam Exchange.
        </p>
        <p className="text-xs text-gray-400 sm:text-sm">© 2025. All rights reserved.</p>
      </footer>
  
      {/* Verify Results Modal */}
      {showVerify && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
          <div className="relative w-full max-w-lg p-4 text-white bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg overflow-y-auto max-h-[90vh]">
            <button
              onClick={() => setShowVerify(false)}
              className="absolute px-2 py-1 text-sm text-gray-300 bg-gray-700 rounded top-4 right-4 hover:bg-gray-600"
            >
              Close
            </button>
            <Verify
              onClose={() => setShowVerify(false)}
              signer={signer}
              contractAddress={blockchainConfig.contractAddress}
            />
          </div>
        </div>
      )}
  
      {/* Verify Modal */}
      {showVerifyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
          <div className="relative w-full max-w-lg p-4 text-white bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg overflow-y-auto max-h-[90vh]">
            <button
              onClick={() => setShowVerifyModal(false)}
              className="absolute px-2 py-1 text-sm text-gray-300 bg-gray-700 rounded top-4 right-4 hover:bg-gray-600"
            >
              Close
            </button>
            {/* You can reuse VerifyModal here if needed */}
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
